import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';
import { DynamicIcon } from './components/icons/DynamicIcon';
import { InventoryAlert } from './components/InventoryAlert';
import { FeedbackButton } from './components/FeedbackButton'; 
import DrinksPage from './pages/DrinksPage';
import DrinkForm from './pages/DrinkForm';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import SignUp from './pages/SignUp';

// Importações de páginas 
import LandingPage from './pages/LandingPage';
import EventPour from './pages/EventPour';
import Login from './pages/Login';
import SimpleTest from './pages/SimpleTest';
import InventoryManagement from './pages/InventoryManagement';
import FeedbackPage from './pages/FeedbackPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import { DrinkUsageReport } from './components/DrinkUsageReport';

// Componente de Loading simples
const SimpleLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600">Carregando...</p>
    </div>
  </div>
);

// Componente de Erro simples
const SimpleError: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center max-w-md w-full mx-4">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="text-red-500 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Erro de Aplicação
        </h3>
        <p className="text-red-600 mb-6 text-sm">
          {error}
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onRetry}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Tentar Novamente
          </button>
          <button
            onClick={() => window.location.href = '/test-simple'}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Página de Teste
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Dashboard Simples de Emergência
const EmergencyDashboard: React.FC = () => {
  const { user, signOut } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-gray-900">EventPour - Dashboard</h1>
            <button
              onClick={signOut}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 ml-4"
            >
              Sair
            </button>
          </div>
        </div>
      </div>
      
      {/* Fixed Feedback Button */}
      <FeedbackButton variant="fixed" />
      
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">✅ Sistema Funcionando!</h2>
            <p className="text-gray-600 mb-4">
              Esta é uma versão de emergência do dashboard com funcionalidades básicas.
            </p>
            <div className="space-y-2">
              <p><strong>Usuário:</strong> {user?.email}</p>
              <p><strong>Status:</strong> Conectado</p>
              <p><strong>Versão:</strong> Emergência v1.0</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6 hover:shadow-md transition-shadow">
              <a href="/inventory" className="block">
                <h3 className="font-semibold text-blue-900 mb-2">📦 Gestão de Estoque</h3>
                <p className="text-blue-700 text-sm">
                  Gerencie o estoque de ingredientes e receba alertas de baixo estoque.
                </p>
              </a>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-6 hover:shadow-md transition-shadow">
              <a href="/feedback" className="block">
                <h3 className="font-semibold text-purple-900 mb-2">💬 Feedback</h3>
                <p className="text-purple-700 text-sm">
                  Envie sugestões, reporte problemas ou compartilhe sua experiência.
                </p>
              </a>
            </div>
            
            <div className="bg-indigo-50 rounded-lg p-6 hover:shadow-md transition-shadow">
              <a href="/drinks" className="block">
                <h3 className="font-semibold text-indigo-900 mb-2">🍹 Drinks Populares</h3>
                <p className="text-indigo-700 text-sm">
                  Veja quais drinks são mais pedidos nos orçamentos.
                </p>
              </a>
            </div>
            
            <div className="bg-green-50 rounded-lg p-6 hover:shadow-md transition-shadow">
              <h3 className="font-semibold text-green-900 mb-2">✅ Funcionalidades Básicas</h3>
              <ul className="text-green-700 text-sm space-y-1">
                <li>• <a href="/calcbar" className="hover:underline">Calculadora de Orçamentos</a></li>
                <li>• <a href="/inventory" className="hover:underline">Gestão de Estoque</a></li>
                <li>• <a href="/drinks" className="hover:underline">Drinks Populares</a></li>
                <li>• <a href="/feedback" className="hover:underline">Enviar Feedback</a></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">🚀 Próximos Passos</h4>
            <div className="text-yellow-700 text-sm space-y-1">
              <p>1. Verifique se esta página carrega corretamente</p>
              <p>2. Teste o login/logout</p>
              <p>3. Acesse <a href="/test-simple" className="underline">/test-simple</a> para testes básicos</p>
              <p>4. Experimente a <a href="/inventory" className="underline">Gestão de Estoque</a></p>
              <p>5. Use a <a href="/calcbar" className="underline">Calculadora de Orçamentos</a></p>
              <p>6. Veja os <a href="/drinks" className="underline">Drinks Populares</a></p>
              <p>7. Envie <a href="/feedback" className="underline">Feedback</a> sobre sua experiência</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de Rota Privada Simplificado
const SimplePrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, error, clearError } = useAuth();

  // if (loading) {
  //   return <SimpleLoading />;
  // }

  if (error) {
    return <SimpleError error={error} onRetry={clearError} />;
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// App Principal Simplificado
function EmergencyApp() {
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/test-simple" element={<SimpleTest />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/calcbar" element={
          <SimplePrivateRoute>
            <EventPour />
          </SimplePrivateRoute>
        } />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/inventory" element={
          <SimplePrivateRoute>
            <InventoryManagement />
          </SimplePrivateRoute>
        } />
        <Route path="/feedback" element={
          <SimplePrivateRoute>
            <FeedbackPage />
          </SimplePrivateRoute>
        } />
        <Route path="/dashboard" element={
          <SimplePrivateRoute>
            <Dashboard />
          </SimplePrivateRoute>
        } />
        <Route path="/drinks/novo" element={
          <SimplePrivateRoute>
            <DrinkForm />
          </SimplePrivateRoute>
        } />
        <Route path="/profile" element={
          <SimplePrivateRoute>
            <Profile />
          </SimplePrivateRoute>
        } />
        <Route path="/drinks" element={
          <SimplePrivateRoute>
            <DrinksPage />
          </SimplePrivateRoute>
        } />
        <Route path="/home" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Página não encontrada</h1>
              <p className="text-gray-600 mb-4">Esta página não existe no modo de emergência.</p>
              <div className="space-x-4">
                <a href="/dashboard" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Dashboard
                </a>
                <a href="/calcbar" className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                  Calculadora
                </a>
                <a href="/inventory" className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                  Gestão de Estoque
                </a>
                <a href="/drinks" className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                  Drinks Populares
                </a>
                <a href="/feedback" className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                  Feedback
                </a>
              </div>
            </div>
          </div>
        } />
      </Routes>
      
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  );
}

export default EmergencyApp;